(() => {
  'use strict';

  const existing = window.GLLFFieldNotes;
  if (existing && existing.version) {
    existing.init(document);
    return;
  }

  const VERSION = '1.0.0';
  const STORAGE = Object.freeze({
    saved: 'gllf.fieldNotes.saved.v1',
    progress: 'gllf.fieldNotes.progress.v1',
    lastVisit: 'gllf.fieldNotes.lastVisit.v1',
    readerPath: 'gllf.fieldNotes.readerPath.v1',
    lastOpened: 'gllf.fieldNotes.lastOpened.v1'
  });

  const instances = new Set();

  const readJSON = (key, fallback) => {
    try {
      const value = window.localStorage.getItem(key);
      return value ? JSON.parse(value) : fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const writeJSON = (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const writeText = (key, value) => {
    try {
      window.localStorage.setItem(key, String(value));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const readText = (key) => {
    try {
      return window.localStorage.getItem(key) || '';
    } catch (_error) {
      return '';
    }
  };

  const dispatch = (name, detail) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const normalizeArticle = (value) => ({
    id: String(value.id || ''),
    url: String(value.url || ''),
    title: String(value.title || 'Untitled Field Note'),
    image: String(value.image || ''),
    topic: String(value.topic || ''),
    publishedAt: String(value.publishedAt || ''),
    savedAt: Number(value.savedAt || Date.now())
  });

  const getSaved = () => {
    const value = readJSON(STORAGE.saved, []);
    return Array.isArray(value) ? value.map(normalizeArticle).filter((note) => note.url) : [];
  };

  const setSaved = (notes) => {
    const unique = [];
    const seen = new Set();
    notes.forEach((note) => {
      const normalized = normalizeArticle(note);
      if (!normalized.url || seen.has(normalized.url)) return;
      seen.add(normalized.url);
      unique.push(normalized);
    });
    return writeJSON(STORAGE.saved, unique.slice(0, 60));
  };

  const getProgress = () => {
    const value = readJSON(STORAGE.progress, {});
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  };

  const articleFromCard = (card) => normalizeArticle({
    id: card.dataset.articleId,
    url: card.dataset.articleUrl,
    title: card.dataset.articleTitle,
    image: card.dataset.articleImage,
    topic: card.dataset.articleTopic,
    publishedAt: card.dataset.publishedAt
  });

  const makeImage = (article, className) => {
    if (!article.image) {
      const placeholder = document.createElement('span');
      placeholder.className = `${className || ''} gllf-shelf-note__placeholder`.trim();
      placeholder.setAttribute('aria-hidden', 'true');
      return placeholder;
    }
    const image = document.createElement('img');
    image.className = className || '';
    image.src = article.image;
    image.alt = '';
    image.loading = 'lazy';
    image.width = 58;
    image.height = 58;
    return image;
  };

  class FieldNotesExperience {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.mode || 'feed';
      this.previousVisit = Number(readText(STORAGE.lastVisit) || 0);
      this.searchController = null;
      this.searchTimer = 0;
      this.searchItems = [];
      this.searchIndex = -1;
      this.article = null;
      this.progressFrame = 0;
      this.lastProgressWrite = 0;
      this.lastCalculatedProgress = 0;
      this.visitTimer = 0;
      this.lifecycleController = new AbortController();
    }

    listen(target, type, callback, options = {}) {
      const normalized = typeof options === 'boolean' ? { capture: options } : options;
      target.addEventListener(type, callback, {
        ...normalized,
        signal: this.lifecycleController.signal
      });
    }

    destroy() {
      this.lifecycleController.abort();
      if (this.searchController) this.searchController.abort();
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.visitTimer);
      if (this.progressFrame) window.cancelAnimationFrame(this.progressFrame);
      instances.delete(this);
    }

    init() {
      if (this.root.dataset.gllfReady === 'true') return;
      this.root.dataset.gllfReady = 'true';
      this.root.classList.add('is-enhanced');

      this.bindNewsletterTopics();
      this.bindCardActions();

      if (this.mode === 'article') {
        this.initArticle();
      } else {
        this.initFeed();
      }
    }

    initFeed() {
      this.bindSearch();
      this.bindLoadMore();
      this.bindReaderPaths();
      this.bindShelf();
      this.markNewNotes(this.root);
      this.updateSaveButtons();
      this.updateSavedCount();

      this.visitTimer = window.setTimeout(() => writeText(STORAGE.lastVisit, Date.now()), 800);
    }

    bindNewsletterTopics() {
      this.root.querySelectorAll('[data-notes-newsletter]').forEach((wrap) => {
        const hidden = wrap.querySelector('[data-newsletter-tags]');
        if (!hidden || hidden.dataset.bound === 'true') return;
        hidden.dataset.bound = 'true';
        const topics = Array.from(wrap.querySelectorAll('[data-newsletter-topic]'));
        const sync = () => {
          const base = (hidden.dataset.baseTags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
          const selected = topics.filter((input) => input.checked).map((input) => input.value);
          hidden.value = [...new Set([...base, ...selected])].join(', ');
        };
        topics.forEach((input) => this.listen(input, 'change', sync));
        sync();

        const success = wrap.querySelector('[data-newsletter-success]');
        if (success) success.focus({ preventScroll: true });
      });
    }

    bindCardActions() {
      this.listen(this.root, 'click', (event) => {
        const saveButton = event.target.closest('[data-save-note]');
        if (saveButton && this.root.contains(saveButton)) {
          const card = saveButton.closest('[data-gllf-note-card]');
          if (card) this.toggleSaved(articleFromCard(card));
          return;
        }

        const noteLink = event.target.closest('[data-note-open]');
        if (noteLink && this.root.contains(noteLink)) {
          const card = noteLink.closest('[data-gllf-note-card]');
          const featured = noteLink.closest('[data-featured-note]');
          const article = card
            ? articleFromCard(card)
            : normalizeArticle({ url: noteLink.getAttribute('href'), title: noteLink.textContent.trim() });
          if (featured && !article.image) {
            const image = featured.querySelector('img');
            if (image) article.image = image.currentSrc || image.src;
          }
          writeJSON(STORAGE.lastOpened, { ...article, openedAt: Date.now() });
        }
      });
    }

    toggleSaved(article) {
      const saved = getSaved();
      const index = saved.findIndex((note) => note.url === article.url);
      let isSaved = false;

      if (index >= 0) {
        saved.splice(index, 1);
        dispatch('gllf:field-notes:unsave', { article });
      } else {
        saved.unshift({ ...article, savedAt: Date.now() });
        isSaved = true;
        dispatch('gllf:field-notes:save', { article });
      }

      setSaved(saved);
      this.refreshStorageUI();
      instances.forEach((instance) => {
        if (instance !== this) instance.refreshStorageUI();
      });
      dispatch('gllf:field-notes:storage-change', { type: 'saved', article, isSaved });
    }

    refreshStorageUI() {
      this.updateSaveButtons();
      this.updateSavedCount();
      this.renderShelf();
      this.updateArticleButtons();
    }

    updateSaveButtons() {
      const savedUrls = new Set(getSaved().map((note) => note.url));
      this.root.querySelectorAll('[data-gllf-note-card]').forEach((card) => {
        const button = card.querySelector('[data-save-note]');
        if (!button) return;
        const isSaved = savedUrls.has(card.dataset.articleUrl);
        button.setAttribute('aria-pressed', String(isSaved));
        button.setAttribute('aria-label', `${isSaved ? 'Remove' : 'Save'} ${card.dataset.articleTitle}`);
        const label = button.querySelector('[data-save-label]');
        if (label) label.textContent = isSaved ? 'Saved note' : 'Save note';
      });
    }

    updateSavedCount() {
      const count = getSaved().length;
      this.root.querySelectorAll('[data-saved-count]').forEach((element) => {
        element.textContent = String(count);
      });
    }

    markNewNotes(scope) {
      if (this.root.dataset.showNewBadges !== 'true' || !this.previousVisit) return;
      scope.querySelectorAll('[data-gllf-note-card]').forEach((card) => {
        const published = Date.parse(card.dataset.publishedAt || '');
        const badge = card.querySelector('[data-new-badge]');
        if (badge && Number.isFinite(published) && published > this.previousVisit) badge.hidden = false;
      });
    }

    bindReaderPaths() {
      const stored = readJSON(STORAGE.readerPath, null);
      if (stored && stored.id) {
        const selected = Array.from(this.root.querySelectorAll('[data-reader-path]'))
          .find((path) => path.dataset.readerPath === String(stored.id));
        if (selected) {
          selected.classList.add('is-returning');
          const label = selected.querySelector('[data-returning-label]');
          if (label) label.hidden = false;
        }
      }

      this.root.querySelectorAll('[data-reader-path]').forEach((path) => {
        const link = path.querySelector('a');
        if (!link) return;
        this.listen(link, 'click', () => {
          writeJSON(STORAGE.readerPath, {
            id: path.dataset.readerPath,
            title: link.textContent.trim(),
            selectedAt: Date.now()
          });
        });
      });
    }

    bindShelf() {
      const shelf = this.root.querySelector('[data-notes-shelf]');
      if (!shelf) return;
      const open = this.root.querySelector('[data-open-notes-shelf]');
      const close = shelf.querySelector('[data-close-notes-shelf]');
      const closeShelf = () => {
        if (typeof shelf.close === 'function') shelf.close();
        else shelf.removeAttribute('open');
      };

      if (open) {
        this.listen(open, 'click', () => {
          this.renderShelf();
          if (typeof shelf.showModal === 'function') shelf.showModal();
          else shelf.setAttribute('open', '');
        });
      }

      if (close) this.listen(close, 'click', closeShelf);
      this.listen(shelf, 'click', (event) => {
        if (event.target === shelf) closeShelf();
        const remove = event.target.closest('[data-remove-saved]');
        if (remove) {
          const article = getSaved().find((note) => note.url === remove.dataset.removeSaved);
          if (article) this.toggleSaved(article);
        }
      });
    }

    renderShelf() {
      const shelf = this.root.querySelector('[data-notes-shelf]');
      if (!shelf) return;

      const savedWrap = shelf.querySelector('[data-saved-notes]');
      const empty = shelf.querySelector('[data-saved-empty]');
      const saved = getSaved().sort((a, b) => b.savedAt - a.savedAt);
      if (savedWrap) {
        savedWrap.replaceChildren();
        saved.forEach((article) => savedWrap.append(this.createShelfRow(article)));
      }
      if (empty) empty.hidden = saved.length > 0;

      const progress = Object.values(getProgress())
        .filter((item) => item && item.url && Number(item.percent) > 2 && Number(item.percent) < 100)
        .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
      const lastOpened = readJSON(STORAGE.lastOpened, null);
      const continueArticle = progress[0] || (lastOpened && lastOpened.url ? { ...lastOpened, percent: 1 } : null);
      const continueWrap = shelf.querySelector('[data-continue-wrap]');
      const continueTarget = shelf.querySelector('[data-continue-reading]');

      if (continueWrap && continueTarget && continueArticle) {
        continueTarget.replaceChildren(this.createContinueRow(continueArticle));
        continueWrap.hidden = false;
      } else if (continueWrap) {
        continueWrap.hidden = true;
      }
    }

    createShelfRow(article) {
      const row = document.createElement('div');
      row.className = 'gllf-shelf-note';
      row.append(makeImage(article));

      const copy = document.createElement('div');
      const link = document.createElement('a');
      link.href = article.url;
      link.textContent = article.title;
      const meta = document.createElement('small');
      meta.textContent = article.topic || 'Lavender Field Note';
      copy.append(link, meta);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.removeSaved = article.url;
      remove.setAttribute('aria-label', `Remove ${article.title} from saved notes`);
      remove.textContent = '×';

      row.append(copy, remove);
      return row;
    }

    createContinueRow(article) {
      const row = document.createElement('div');
      row.className = 'gllf-continue-note';
      row.append(makeImage(article));

      const copy = document.createElement('div');
      const link = document.createElement('a');
      link.href = article.url;
      link.textContent = article.title || 'Continue reading';
      const meta = document.createElement('small');
      const percent = Math.max(1, Math.min(99, Math.round(Number(article.percent) || 1)));
      meta.textContent = percent > 1 ? `${percent}% read` : 'Opened recently';
      copy.append(link, meta);

      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      row.append(copy, arrow);
      return row;
    }

    bindSearch() {
      const form = this.root.querySelector('[data-notes-search]');
      if (!form) return;
      const input = form.querySelector('[data-notes-search-input]');
      const results = form.querySelector('[data-notes-search-results]');
      const status = form.querySelector('[data-search-status]');
      if (!input || !results) return;

      this.listen(input, 'input', () => {
        window.clearTimeout(this.searchTimer);
        if (this.searchController) {
          this.searchController.abort();
          this.searchController = null;
        }
        const query = input.value.trim();
        if (query.length < 2) {
          this.closeSearch(input, results);
          return;
        }
        this.searchTimer = window.setTimeout(() => this.fetchSuggestions(query, input, results, status), 180);
      });

      this.listen(input, 'keydown', (event) => {
        if (!this.searchItems.length) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.searchIndex = (this.searchIndex + 1) % this.searchItems.length;
          this.setActiveSearchItem(input);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.searchIndex = (this.searchIndex - 1 + this.searchItems.length) % this.searchItems.length;
          this.setActiveSearchItem(input);
        } else if (event.key === 'Enter' && this.searchIndex >= 0) {
          event.preventDefault();
          this.searchItems[this.searchIndex].click();
        } else if (event.key === 'Escape') {
          this.closeSearch(input, results);
        }
      });

      this.listen(document, 'click', (event) => {
        if (!form.contains(event.target)) this.closeSearch(input, results);
      });
    }

    async fetchSuggestions(query, input, results, status) {
      if (this.searchController) this.searchController.abort();
      this.searchController = new AbortController();
      const routeRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const url = new URL(`${routeRoot}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'article');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][fields]', 'title,body,tag,author');

      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: this.searchController.signal
        });
        if (!response.ok) throw new Error(`Search returned ${response.status}`);
        const payload = await response.json();
        const all = payload && payload.resources && payload.resources.results
          ? payload.resources.results.articles || []
          : [];
        const prefix = this.root.dataset.blogPrefix || '';
        const matches = all.filter((article) => {
          const articleUrl = String(article.url || '').split('?')[0];
          return articleUrl.startsWith(`${prefix}/`);
        });
        this.renderSuggestions(matches, query, input, results, status);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.closeSearch(input, results);
          if (status) status.textContent = 'Search suggestions are unavailable. Submit the form for full search results.';
        }
      }
    }

    renderSuggestions(matches, query, input, results, status) {
      results.replaceChildren();
      this.searchItems = [];
      this.searchIndex = -1;

      if (!matches.length) {
        const empty = document.createElement('p');
        empty.className = 'gllf-notes-search__empty';
        empty.textContent = `No Lavender Field Notes found for “${query}”.`;
        results.append(empty);
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        if (status) status.textContent = 'No suggested Field Notes found.';
        return;
      }

      const list = document.createElement('ul');
      matches.forEach((article, index) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.id = `GLLFNoteSuggestion-${this.root.id}-${index}`;
        link.href = article.url;
        link.setAttribute('role', 'option');

        if (article.image) {
          const image = document.createElement('img');
          image.src = article.image;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 48;
          image.height = 48;
          link.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'gllf-search-image-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          link.append(placeholder);
        }

        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = article.title;
        const meta = document.createElement('small');
        meta.textContent = article.author ? `By ${article.author}` : 'Lavender Field Note';
        copy.append(title, meta);
        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        link.append(copy, arrow);
        item.append(link);
        list.append(item);
      });

      results.append(list);
      this.searchItems = Array.from(list.querySelectorAll('a'));
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      if (status) status.textContent = `${matches.length} suggested Field Note${matches.length === 1 ? '' : 's'} found.`;
    }

    setActiveSearchItem(input) {
      this.searchItems.forEach((item, index) => {
        const active = index === this.searchIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
      const active = this.searchItems[this.searchIndex];
      if (active) input.setAttribute('aria-activedescendant', active.id);
    }

    closeSearch(input, results) {
      if (this.searchController) {
        this.searchController.abort();
        this.searchController = null;
      }
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      this.searchItems = [];
      this.searchIndex = -1;
    }

    bindLoadMore() {
      const pagination = this.root.querySelector('[data-notes-pagination]');
      const button = pagination ? pagination.querySelector('[data-load-more]') : null;
      if (!pagination || !button || !pagination.dataset.nextUrl) return;
      pagination.classList.add('is-load-more');
      this.listen(button, 'click', () => this.loadMore(pagination, button));
    }

    async loadMore(pagination, button) {
      const nextUrl = pagination.dataset.nextUrl;
      const grid = this.root.querySelector('[data-gllf-feed-grid]');
      const status = pagination.querySelector('[data-load-status]');
      if (!nextUrl || !grid || button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      grid.setAttribute('aria-busy', 'true');
      const originalLabel = button.querySelector('span');
      if (originalLabel) originalLabel.textContent = 'Gathering more notes…';

      try {
        const response = await fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Pagination returned ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incomingRoot = doc.querySelector('[data-gllf-field-notes][data-mode="feed"]');
        const incomingGrid = incomingRoot ? incomingRoot.querySelector('[data-gllf-feed-grid]') : null;
        const incomingPagination = incomingRoot ? incomingRoot.querySelector('[data-notes-pagination]') : null;
        if (!incomingGrid) throw new Error('No article grid found in next page');

        const cards = Array.from(incomingGrid.querySelectorAll('[data-gllf-note-card]'));
        cards.forEach((card) => grid.append(document.importNode(card, true)));
        this.markNewNotes(grid);
        this.updateSaveButtons();

        const currentPage = incomingPagination ? incomingPagination.dataset.currentPage : '';
        const totalPages = incomingPagination ? incomingPagination.dataset.totalPages : pagination.dataset.totalPages;
        const incomingNext = incomingPagination ? incomingPagination.dataset.nextUrl : '';
        pagination.dataset.currentPage = currentPage;
        pagination.dataset.nextUrl = incomingNext || '';

        if (incomingNext) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (originalLabel) originalLabel.textContent = 'Load more Field Notes';
          if (status) status.textContent = `${cards.length} more notes loaded. Page ${currentPage} of ${totalPages}.`;
        } else {
          button.hidden = true;
          if (status) status.textContent = `All Field Notes loaded. ${totalPages} page${Number(totalPages) === 1 ? '' : 's'} total.`;
        }
      } catch (_error) {
        pagination.classList.remove('is-load-more');
        button.hidden = true;
        if (status) status.textContent = 'More notes could not be loaded here. Use the page links now shown.';
      } finally {
        grid.removeAttribute('aria-busy');
        button.removeAttribute('aria-busy');
      }
    }

    initArticle() {
      const payload = this.root.querySelector('[data-article-payload]');
      if (!payload) return;
      try {
        this.article = normalizeArticle(JSON.parse(payload.textContent));
      } catch (_error) {
        return;
      }

      this.bindArticleTools();
      this.updateSaveButtons();
      this.updateArticleButtons();
      this.trackReadingProgress();
      writeJSON(STORAGE.lastOpened, { ...this.article, openedAt: Date.now() });
    }

    bindArticleTools() {
      const save = this.root.querySelector('[data-article-save]');
      const complete = this.root.querySelector('[data-mark-complete]');
      const share = this.root.querySelector('[data-share-note]');
      const print = this.root.querySelector('[data-print-note]');
      const status = this.root.querySelector('[data-article-tool-status]');

      if (save) this.listen(save, 'click', () => this.toggleSaved(this.article));
      if (complete) {
        this.listen(complete, 'click', () => {
          const progress = getProgress();
          const current = progress[this.article.url] || {};
          const wasComplete = Number(current.percent) >= 100;
          progress[this.article.url] = {
            ...this.article,
            percent: wasComplete ? 0 : 100,
            updatedAt: Date.now()
          };
          writeJSON(STORAGE.progress, progress);
          this.lastCalculatedProgress = wasComplete ? 0 : 100;
          this.updateArticleButtons();
          if (status) status.textContent = wasComplete ? 'Marked as unread.' : 'Marked as read.';
          dispatch('gllf:field-notes:progress', { article: this.article, percent: wasComplete ? 0 : 100 });
        });
      }
      if (share) {
        this.listen(share, 'click', async () => {
          const absoluteUrl = new URL(this.article.url, window.location.origin).href;
          try {
            if (navigator.share) {
              await navigator.share({ title: this.article.title, url: absoluteUrl });
              if (status) status.textContent = 'Share options opened.';
            } else if (navigator.clipboard) {
              await navigator.clipboard.writeText(absoluteUrl);
              if (status) status.textContent = 'Field Note link copied.';
            } else if (status) {
              status.textContent = 'Copy the page address from your browser to share this Field Note.';
            }
          } catch (error) {
            if (error.name !== 'AbortError' && status) status.textContent = 'The share action was not completed.';
          }
        });
      }
      if (print) this.listen(print, 'click', () => window.print());
    }

    updateArticleButtons() {
      if (!this.article) return;
      const isSaved = getSaved().some((note) => note.url === this.article.url);
      const save = this.root.querySelector('[data-article-save]');
      if (save) {
        save.setAttribute('aria-pressed', String(isSaved));
        const label = save.querySelector('[data-article-save-label]');
        if (label) label.textContent = isSaved ? 'Saved' : 'Save this note';
      }

      const progress = getProgress()[this.article.url];
      const isComplete = progress && Number(progress.percent) >= 100;
      const complete = this.root.querySelector('[data-mark-complete]');
      if (complete) {
        complete.setAttribute('aria-pressed', String(Boolean(isComplete)));
        const label = complete.querySelector('[data-complete-label]');
        if (label) label.textContent = isComplete ? 'Read' : 'Mark as read';
      }
    }

    trackReadingProgress() {
      const bar = this.root.querySelector('[data-reading-progress-bar]');
      if (!bar) return;
      const target = document.querySelector('main article:not(.gllf-note-card)')
        || document.querySelector('.article-template')
        || document.querySelector('main');

      const update = () => {
        this.progressFrame = 0;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        let start = 0;
        let distance = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        if (target) {
          const rect = target.getBoundingClientRect();
          start = scrollTop + rect.top - window.innerHeight * 0.2;
          distance = Math.max(1, target.offsetHeight - window.innerHeight * 0.55);
        }
        const raw = ((scrollTop - start) / distance) * 100;
        const percent = Math.max(0, Math.min(100, raw));
        this.lastCalculatedProgress = percent;
        bar.style.transform = `scaleX(${percent / 100})`;

        const now = Date.now();
        if (now - this.lastProgressWrite > 900) {
          this.lastProgressWrite = now;
          this.saveProgress(percent);
        }
      };

      const requestUpdate = () => {
        if (this.progressFrame) return;
        this.progressFrame = window.requestAnimationFrame(update);
      };

      this.listen(window, 'scroll', requestUpdate, { passive: true });
      this.listen(window, 'resize', requestUpdate, { passive: true });
      this.listen(window, 'pagehide', () => {
        this.saveProgress(this.lastCalculatedProgress);
      });
      requestUpdate();
    }

    saveProgress(percent) {
      const progress = getProgress();
      const previous = progress[this.article.url];
      const preservedComplete = previous && Number(previous.percent) >= 100;
      const rounded = preservedComplete ? 100 : Math.max(0, Math.min(99, Math.round(percent)));
      progress[this.article.url] = {
        ...this.article,
        percent: rounded,
        updatedAt: Date.now()
      };

      const entries = Object.entries(progress)
        .sort((a, b) => Number(b[1].updatedAt || 0) - Number(a[1].updatedAt || 0))
        .slice(0, 60);
      writeJSON(STORAGE.progress, Object.fromEntries(entries));
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches && scope.matches('[data-gllf-field-notes]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-gllf-field-notes]'));
    roots.forEach((root) => {
      if (root.dataset.gllfReady === 'true') return;
      const instance = new FieldNotesExperience(root);
      instances.add(instance);
      instance.init();
    });
  };

  window.GLLFFieldNotes = Object.freeze({
    version: VERSION,
    init,
    getSaved,
    getProgress,
    storageKeys: STORAGE
  });

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance) => {
      if (instance.root === event.target || (event.target.contains && event.target.contains(instance.root))) {
        instance.destroy();
      }
    });
  });
})();
